import { Navigate } from 'react-router';
import { Building2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/auth.store';
import { useMyTenants } from '@/features/me/api';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { PrefetchLink } from '@/components/common/prefetch-link';

const ROL_LABELS: Record<string, string> = {
  admin: 'Administradora',
  comite: 'Comité',
  conserje: 'Conserjería',
  propietario: 'Propietario',
  residente: 'Residente',
};

export function WorkspacePickerRoute() {
  const session = useAuthStore((s) => s.session);
  const { data, isLoading } = useMyTenants();

  if (!session) return null;
  if (session.isSuperAdmin) return <Navigate to="/app/platform" replace />;
  if (isLoading || !data) {
    return (
      <PageContainer width="wide">
        <Skeleton className="h-40" />
      </PageContainer>
    );
  }
  if (data.items.length === 0) return <Navigate to="/app/no-access" replace />;
  if (data.items.length === 1) {
    return <Navigate to={`/app/condominios/${data.items[0]!.tenantId}`} replace />;
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        eyebrow="Tus condominios"
        title="Elige un condominio"
        subtitle="Tienes acceso a varios condominios. Elige uno para continuar."
      />
      <ul className="grid gap-3 sm:grid-cols-2">
        {data.items.map((t) => (
          <li key={t.tenantId}>
            <PrefetchLink
              to={`/app/condominios/${t.tenantId}`}
              className="block rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-6 transition-colors hover:border-[var(--color-primary)]"
            >
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-sm bg-[var(--color-primary-50)] text-[var(--color-primary)]">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">{t.nombre}</h3>
                  <p className="text-xs text-[var(--color-ink-muted)]">{t.comuna}</p>
                  <p className="eyebrow mt-2">{ROL_LABELS[t.rol] ?? t.rol}</p>
                </div>
              </div>
            </PrefetchLink>
          </li>
        ))}
      </ul>
    </PageContainer>
  );
}
